/**
 * Portal language switcher.
 *
 * Renders a small `<select>` that swaps the active i18next language and
 * persists the user's choice to localStorage (`nv_locale_portal`).
 *
 * The portal is auth-bound, so we deliberately do NOT manipulate the URL --
 * locale state stays runtime-only.
 *
 * @module components/LanguageSwitcher
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LOCALES,
  LOCALE_METADATA,
  isSupportedLocale,
  type SupportedLocale,
} from '@noxverify/i18n';

const STORAGE_KEY = 'nv_locale_portal';

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation('portal');

  const current: SupportedLocale = isSupportedLocale(i18n.language)
    ? (i18n.language as SupportedLocale)
    : 'en';

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const next = event.target.value;
      if (!isSupportedLocale(next)) return;
      void i18n.changeLanguage(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // localStorage may be unavailable (Safari private mode, etc.) --
        // language change still applies for the current session.
      }
    },
    [i18n],
  );

  return (
    <label className="inline-flex items-center gap-2 text-xs text-ink-muted">
      <span className="sr-only">{t('footer.language', { defaultValue: 'Language' })}</span>
      <select
        value={current}
        onChange={handleChange}
        aria-label={t('footer.language', { defaultValue: 'Language' })}
        className="rounded-sm border border-hairline-strong bg-white px-2.5 py-1 text-xs text-ink-muted transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        {SUPPORTED_LOCALES.map((loc) => (
          <option key={loc} value={loc}>
            {LOCALE_METADATA[loc].nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}
